import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';
import type { TaskName, TaskSummary } from '@shared/schemas/worker';

/* The queries behind /admin/worker. ROADMAP.md §7.6. */

const ROOT = ['admin', 'worker'] as const;

export function useWorkerStatus() {
  return useQuery({
    queryKey: [...ROOT, 'status'],
    queryFn: () => api.get<{ tasks: TaskSummary[]; recentFailures: number }>('/api/admin/worker'),
    // A dashboard about background work that only updates when you reload is a
    // dashboard people reload. Half a minute is often enough to watch a manual
    // run finish and cheap enough on one small server.
    refetchInterval: 30_000,
  });
}

/**
 * Run a task now — **through the same ledger the schedule uses**.
 *
 * `ran: false` means the run key for this period is already claimed, which is
 * the honest answer rather than a silent success. The screen says so.
 */
export function useRunTask() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (taskName: TaskName) =>
      api.post<{ ran: boolean; result?: unknown }>('/api/admin/worker/run', { taskName }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ROOT });
      // Generation makes jobs; the calendar is looking at them.
      await client.invalidateQueries({ queryKey: ['admin', 'jobs'] });
      await client.invalidateQueries({ queryKey: ['admin', 'recurring-jobs'] });
    },
  });
}
